(ns ncr.fragment.common
  (:require
   [clojure.core.match :refer [match]]
   [cljs.core.async :as async :refer-macros [go]]
   [clojure.string :as str]
   [goog.events.KeyCodes :as KeyCodes]
   [lambdaisland.glogi :as log]
   [tick.core :as t]
   [uix.core.alpha :as uix]
   [ncr.me :as me]
   [ncr.widgets.core :as w]
   [ncr.widgets.input :as wi]
   [ncr.widgets.pf :as wpf]
   [ncr.etc.clipboard :as clipboard]
   [ncr.etc.rhf :as rhf]
   [ncr.etc.modal :as m]
   [ncr.etc.util :as u]
   [ncr.nsql :as nsql]
   [ncr.backend :as b]
   [ncr.etc.pubsub :as ps]
   [ncr.etc.pf :as pf]
   [ncr.etc.alert :as a]
   [ncr.navigate :as n]
   [ncr.stories-util :as stories]
   ["transliteration/dist/browser/bundle.esm.min.js" :refer [slugify]]
   [ncr.etc.profile :as p]
   [ncr.amplitude :as amp]))

(def ^:private max-clipboard-suggestion-length 50)

(defn c-fragment-form-description [{:keys [register-pf watch get-values] :as form} min-height disabled?]
  (let
    [id (uix/memo #(gensym) [])
     descr (uix/state (.-description (get-values)))]

    (watch
      (fn [{:keys [description]} {:keys [name]}]
        (when (= :description name)
          (reset! descr description))))

    [rhf/c-form-group
     {:field-id id
      :label
      [:<>
       "Description"
       [:a
        {:href "https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet"
         :target "_blank"
         :class :ncr-info-icon}
        [:i
         {:icon "information-outline"
          :class [:mdi :mdi-information-outline]}]]]
      :is-required false}
     :description form
     [pf/c-tabs {:default-active-key :markdown}
      [pf/c-tab
       {:title [pf/c-tab-title-text "Markdown"]
        :event-key :markdown}
       [pf/c-text-area
        (merge
          (register-pf :description {:required false})
          {:style {:min-height min-height :resize :vertical}
           :id id
           :is-disabled disabled?})]]

      [pf/c-tab
       {:title [pf/c-tab-title-text "Preview"]
        :event-key :preview}
       [:div.markdown-body
        {:style {:min-height min-height}}
        [pf/c-markdown @descr]]]]]))

(def ^:private name-autocomplete-options
  ["Benz Wein- und Getränkemärkte"
   "Vodafone"
   "Lidl"
   "Amazon"
   "EDEKA"
   "DM"
   "Esso Station"
   "Shell Station"
   "Taxi"
   "Deutsche Post"
   "DHL"
   "Coffee Collective"
   "Müller"])

(defn sanitize-suggestion [s]
  (let [suggestion (some-> s str/trim (str/replace #"[_]{2,}" "_") (str/replace "_" " "))]
    (when (and suggestion (> (count suggestion) 3)) suggestion)))

(defn sanitize-clipboard-suggestion [s]
  (let [suggestion (sanitize-suggestion s)]
    (when (and suggestion (<= (count suggestion) max-clipboard-suggestion-length)) suggestion)))

(defn- get-suggestions [attachments]
  (->> attachments
    (map
      (fn [{:keys [id fileName]}]
        {:id id
         :text
         (as-> fileName $
           (-> $ str/trim (str/split #"\."))
           (filter seq $)
           (if (> (count $) 1) (drop-last $) $)
           (str/join "." $)
           (sanitize-suggestion $))}))
    (filter (comp seq :text))
    (reduce
      (fn [acc {:keys [text] :as attachment}]
        (assoc acc text attachment))
      {})
    vals
    (map :text)))

(defn- get-suggestion-action [on-change submit text]
  (fn [^js e]
    (if (= 2 (.-detail e))
      (submit e)
      (on-change text))))

(defn c-name-input [id {:keys [control submit]} attachments]
  (let [ref (uix/ref)
        *clipboard (uix/state nil)]

    (clipboard/use-read
      #(when-let [s (sanitize-clipboard-suggestion %)]
         (reset! *clipboard s)))

    (uix/with-effect [@ref]
      #(when-let [el @ref] (.focus el)))

    [rhf/c-controller
     {:name :name
      :control control

      :rules
      {:required true
       :validate
       {:not-blank
        (fn [v]
          (or
            (not (str/blank? v))
            "can't be blank"))}}

      :render
      (fn [{{:keys [on-change value]} :field}]
        (let
          [suggestions (concat (when-let [clipboard @*clipboard] [clipboard])
                         (-> attachments get-suggestions))
           actionable-suggestions (map (juxt identity #(get-suggestion-action on-change submit %)) suggestions)]
          [wi/c-autocomplete
           {:id id
            :ref ref
            ;   :options (clj->js name-autocomplete-options)
            :suggestions actionable-suggestions
            :on-change on-change
            :on-key-down #(when (= KeyCodes/ENTER (u/key-code %)) (submit %))
            :value value}]))}]))

(defn- c-fragment-edit [_me {:keys [id description attachments] :as defaults}]
  (let
    [ref (uix/ref)

     {:keys [submit register-pf set-value] :as form}
     (rhf/use-form
       {:shouldFocusError false
        :default-values (select-keys defaults [:name :description])})

     description? (some? description)
     disabled? (uix/state (not description?))
     modal-id :edit-fragment-modal]

    (uix/with-effect [id]
      (let [description (:description defaults)]
        (when (nil? description)
          (go
            (u/when-fail
              [error
               (u/error-m
                 [{:keys [description]}
                  (<! (b/!query [:fragment_load {:reference id} [:description]]))]
                 (set-value :description description {})
                 (reset! disabled? false))]

              (do
                (m/close! nil)
                (a/toast!
                  {:variant :danger
                   :title "Error"
                   :body "Error loading fragment description"})
                (log/error :error error)))))))

    (m/use-submit-form modal-id ref submit)

    (let [disabled? @disabled?]
      [m/c-modal
       {:title "Edit Fragment"
        :variant :medium
        :is-overflow-visible false
        :id modal-id
        :ref ref
        :actions
        [[pf/c-button
          {:variant :primary
           :on-click submit
           :is-disabled disabled?}
          "Ok"]

         [pf/c-button
          {:variant :link :on-click #(m/close! nil)} "Cancel"]]}

       [pf/c-form {:on-submit submit}
        (let [id (uix/memo #(gensym) [])]
          [rhf/c-form-group
           {:label "Name"
            :is-required true
            :field-id id
            :class :ncr-fragment-name-control}
           :name
           form
           [c-name-input id form attachments]])

        [c-fragment-form-description form 200 disabled?]]])))

(defn !update-fragment-name [fragment-id name]
  (go
    (<!
      (a/!operation {:operation-title "Edit Fragment" :success-title "Fragment Updated" :body name}
        (b/!mutation [:reference_update {:id fragment-id :name name}])))))

(defn !fragment-edit [me [fragment]]
  (go
    (if-let [{:keys [name description]} (<! (m/!modal #'c-fragment-edit me fragment))]
      (<!
        (a/!operation {:operation-title "Edit Fragment" :success-title "Fragment Updated" :body name}
          (b/!mutation
            [:reference_update
             {:id (get fragment :id) :name name :description :$description}]
            {:$description description})))
      [:error :cancel])))

(defn fragment-name->slug [s]
  (-> s
    (slugify)
    (str/replace #"_+" "-")
    (str/replace #"\.+" "-")
    (str/replace #"-{2,}" "-")
    (str/replace #"^-" "")
    (str/replace #"-$" "")))


(if stories/STORYBOOK
  (defn- get-href [& _args])
  (defn- get-href [& args]
    (apply n/href args)))

(defn c-name [props {:keys [name writableHint id] :as fragment}]
  (u/without [editable? open-in-new-tab?] props
    (let
      [show-tooltip? (uix/state false)
       ref (uix/ref)
       href (get-href :fragment {:slug (fragment-name->slug name) :id id})

       body
       [pf/c-button
        (w/merge-props
          {:variant :inline
           :style {:width "100%"}
           :class
           [:pf-c-button-selectable-text
            :pf-u-px-0
            (when-not editable? :ncr-c-search-list__item-name)]}

          props

          {:on-click
           (cond
             (not editable?)
             (fn [e]
               (let [r (js/window.getSelection)]
                 (if open-in-new-tab?
                   (u/open-link! href)
                   (when (zero? (- (.-anchorOffset r) (.-focusOffset r)))
                     (let
                       [route-params {:slug (fragment-name->slug name) :id id}
                        open (partial js/window.open (n/fragment-href route-params))
                        track-base
                        {"Origin" "Search" "Destination" "Fragment"}]
                       (u/on-click-modifiers e
                         {:on-ctrl-click
                          (fn [_]
                            (amp/navigate!
                              (merge track-base {"Target" "Blank"}))
                            (open))
                          :on-shift-click
                          (fn [_]
                            (amp/navigate!
                              (merge track-base {"Target" "Blank Window"}))
                            (open "_blank" "top"))
                          :on-click
                          (fn [_]
                            (amp/navigate! track-base)
                            (n/push-fragment! route-params))}))))))

             (and editable? writableHint)
             (let [me (uix/context me/*context*)]
               #(go
                  (u/when-ok [_ (<! (!fragment-edit me [fragment]))]
                    (ps/pub! [:fragment/load false])))))})
        (let
          [name-comp
           [:p
            {:class [:pf-u-float-left :pf-u-text-truncate]
             :ref ref}
            name]]
          (if editable?
            name-comp
            [:a.pf-c-button__link
             {:on-click #(.preventDefault %)
              :href href}
             name-comp]))]]

      (uix/effect!
        (fn []
          (when-some [el @ref]
            (reset! show-tooltip? (> el.scrollWidth el.clientWidth)))))

      [:<>
       (if @show-tooltip?
         [pf/c-tooltip {:content [:span name] :position :auto :enable-flip true}
          body]
         body)])))

(defn c-description [_ {:keys [description]}]
  (let
    [max-lines 3
     lines (-> description (str/split #"\n"))
     md [:div.markdown-body [pf/c-markdown description]]]
    (if (<= (count lines) max-lines)
      md
      (let [toggle (uix/memo #(gensym "toggle") [])
            expanded? @(ps/sub-reducer-map! false {toggle not})]
        [pf/c-expandable-section
         {:is-expanded expanded?
          :toggle-text (if expanded? "Show less" "Show more")
          :variant "truncate"
          :truncate-max-lines max-lines
          :on-toggle #(ps/pub! [toggle])}
         md]))))

(defn folder-icon [{:keys [folderType]}]
  (get
    {"INBOX" :folder-outline
     "SHARED" :folder-account-outline
     "TRASH" :trash-can-outline} folderType))

(defn c-folder [{:keys [folder promotedHint revoked]}]
  (when-let [[icon color label variant tooltip]
             (cond
               revoked
               [:trash-can-outline :red "revoked" nil (some->> revoked t/instant t/zoned-date-time (t/format u/local-date-time-formatter))]
               (and folder (not promotedHint))
               (let [{folder-name :name :as folder} (me/folder-by-id (uix/context me/*context*) folder)]
                 [(folder-icon folder) :cyan folder-name :outline])

               :else nil)]
    [pf/c-tooltip
     (merge
       {:position :auto
        :enable-flip true}
       (if tooltip
         {:content tooltip}
         {:trigger :manual}))
     [pf/c-label
      {:class [:pf-u-font-size-sm]
       :color color
       :variant variant
       :href (get-href :search {:query (str "in:" label)})
       :icon [w/c-mdi-icon {:icon icon}]}
      label]]))

(defn- !label-remove [_me {fragment-id :id fragment-name :name} {label-id :id}]
  (a/!operation
    {:operation-title "Remove Label"
     :success-title "Label Removed"
     :body fragment-name}
    (b/!mutation
      [:reference_edit_labels
       {:id fragment-id
        :op :remove
        :labels [label-id]}])))

(defn c-labels [props {:keys [labels writableHint] :as fragment}]
  (u/without [editable? open-in-new-tab? num-labels] props
    (let
      [me (uix/context me/*context*)
       label-by-id (partial me/label-by-id me)]
      (when (seq labels)
        [wpf/c-label-group
         {:num-labels num-labels}
         (for
           [{label-name :name id :id :as label}
            (->> labels
              (map label-by-id)
              (filter #(seq (:id %)))
              (sort-by :name))]
           [pf/c-label
            (w/merge-props
              {:class [:pf-u-font-size-sm]
               :key id
               :variant :outline
               :color :blue
               :style {:cursor :pointer}}
              props

              {:on-close
               (when (and editable? writableHint)
                 (fn [e]
                   (u/stop-propagation e)
                   (go
                     (u/when-ok [_ (<! (!label-remove me fragment label))]
                       (ps/pub! [:fragment/load false])))))

               :on-click
               (fn [e]
                 (let [query (str "label:" (nsql/maybe-quote label-name))]
                   (if open-in-new-tab?
                     (u/open-link! (n/search-href query))
                     (let [track-origin (get props :track-origin)
                           track-base
                           (when (some? track-origin)
                             {"Query" query "Origin" track-origin})]
                       (u/on-click-modifiers e
                         {:on-click
                          (fn []
                            (when (some? track-base)
                              (amp/search! track-base))
                            (n/push-search! query))
                          :on-ctrl-click
                          (fn []
                            (when (some? track-base)
                              (amp/search! (merge track-base {"Modify" "Toggle"})))
                            (ps/pub! [:query/toggle query]))})))))})
            label-name])]))))

(defn c-date [props fragment]
  (when-let [timestamp (get-in fragment [:referenceTimestamps :created])]
    [:p.pf-u-font-size-xs
     [w/c-date-distance timestamp]]))

(defn- !quick-set-revoke [revoke me fragments]
  (go
    (when
      (<!
        (a/!fragment-operation
          {:operation-title (if revoke "Revoking" "Returning to Accepted")
           :success-title (if revoke "Revoked" "Returned to Accepted")
           :fragments fragments}

          (b/!multi-mutation
            (map
              (fn [{:keys [id]}]
                [(keyword id)
                 [:reference_set_revoked
                  {:id id :revoked revoke}]])
              fragments))))
      (ps/pub! [:me/load]))))

(def !quick-revoke (partial !quick-set-revoke true))
(def !quick-unrevoke (partial !quick-set-revoke false))

(defn update-props [updated body fragment-id]
  (let [ch (async/chan)]
    (go
      (let
        [patched
         (<!
           (a/!operation
             {:operation-title "Updating Property"
              :success-title "Property Updated"
              :body body}
             (b/!mutation
               [:properties_patch
                {:reference fragment-id
                 :properties (u/transit-write updated)}])))
         patched (u/transit-read patched)]
        (ps/pub! [:fragment/properties-update (fn [original] (merge original patched))])
        (async/put! ch (if (= :error (first patched)) :error :success))))
    ch))
